/**
 * One cache for the whole app.
 *
 * Every screen used to hold its own copy of the same idea: a `useState`, a
 * `useEffect`, a `load()`, the word "Loading" in the middle of the screen, and
 * a `setErr`. Seventy-nine effects and a hundred and eighty lines of loaded /
 * error state, none of it shared — so moving between Home and Tasks fetched
 * everything again from nothing, and going back showed an empty screen a
 * second time. On a phone on a cellular connection that reads as an app that
 * stalls on every touch.
 *
 * The defaults below are chosen for a household app rather than a dashboard:
 * data is worth showing immediately and worth checking quietly, and a failure
 * the server has already explained is not worth retrying.
 */
import { QueryClient } from '@tanstack/react-query';
import { ApiError, api } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Thirty seconds matches what the today screen already polled at, and it
      // is roughly how long a household's answer stays true.
      staleTime: 30_000,
      // Put the phone down, pick it up, and the screen is current. This is the
      // single behaviour that makes a cached app feel live rather than stale.
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // A 4xx is the server telling us something it will tell us again. Only
      // network and 5xx failures are worth a second attempt.
      retry: (count, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return count < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/** `useQuery({ queryKey: ['/v1/staff'], queryFn: get })` — the path is the key. */
export function get<T>({ queryKey }: { queryKey: readonly unknown[] }): Promise<T> {
  return api<T>(String(queryKey[0]));
}

/**
 * Everything under this path is now wrong.
 *
 * Approving a task changes the task, the queue it was in, the person's page
 * and the count on the home screen. Rather than name all four at every call
 * site, invalidate the prefix and let the screens that are mounted refetch.
 */
export function invalidate(...prefixes: string[]): void {
  for (const p of prefixes) {
    void queryClient.invalidateQueries({
      predicate: (q) => String(q.queryKey[0] ?? '').startsWith(p),
    });
  }
}
